import { Component, SimpleChanges } from '@angular/core';
import { Task } from '@app/_components/task';
import { TaskService } from '@app/_components/task';
import { AuthenticationService, UserService, AlertService } from '@app/_services';
import * as moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { BaseTaskViewComponent } from '../base-task-view.component';
import { first } from 'rxjs/operators';
import { Location } from '@angular/common';

const DAYS_VIEW = 10;

@Component({
    selector: 'app-task-gantt-view',
    templateUrl: './task-gantt-view.component.html',
    styleUrls: ['./task-gantt-view.component.css'],
    standalone: false
})
export class TaskGanttViewComponent extends BaseTaskViewComponent {
  // Gantt chart data
  ganttTasks: any[] = [];
  startDate: moment.Moment;
  endDate: moment.Moment;
  days: moment.Moment[] = [];
  viewStartDate: moment.Moment;

  // Loading state
  loading: boolean = false;

  // Context menu
  items: any[] = [];

  constructor(
    authenticationService: AuthenticationService,
    taskService: TaskService,
    userService: UserService,
    alertService: AlertService,
    dialog: MatDialog,
    location: Location
  ) {
    super(authenticationService, taskService, userService, alertService, dialog, location);
  }

  protected onInitView(): void {
    this.setInitialViewWindow();
    this.loadTasks();
    this.initializeContextMenu();
  }

  private setInitialViewWindow(): void {
    this.viewStartDate = moment().startOf('day');
  }

  goPrev10Days(): void {
    this.viewStartDate = this.viewStartDate.clone().subtract(DAYS_VIEW, 'days');
    this.loadTasks();
  }

  goNext10Days(): void {
    this.viewStartDate = this.viewStartDate.clone().add(DAYS_VIEW, 'days');
    this.loadTasks();
  }

  get viewRangeLabel(): string {
    if (!this.viewStartDate || !this.days.length) return '';
    const start = this.viewStartDate.format('MMM D');
    const end = this.viewStartDate.clone().add(DAYS_VIEW - 1, 'days').format('MMM D, YYYY');
    return `${start} – ${end}`;
  }

  private initializeContextMenu(): void {
    this.items = [
      {
        label: 'Copy Task',
        icon: 'pi pi-fw pi-copy',
        command: (event) => this.openTaskForClone()
      },
      {
        label: 'Sub Task',
        icon: 'pi pi-fw pi-clone',
        command: (event) => this.openTaskForSubTask()
      },
      {
        label: 'Mail Task',
        icon: 'pi pi-fw pi-envelope',
        command: (event) => this.openMailTask()
      },
      {
        label: 'Summary',
        icon: 'pi pi-fw pi-search',
        command: (event) => this.openSummary()
      },
      {
        label: 'Status Update req',
        icon: 'pi pi-question',
        command: (event) => this.sendStatusUpdateReq(this.selectedTask)
      },
    ];
  }

  protected onViewChanges(changes: SimpleChanges): void {
    // Load tasks dynamically when filters, taskTag, or groupBy change
    if (changes['filters'] || changes['taskTag'] || changes['groupBy']) {
      this.loadTasks();
    } else if (changes['tasks']) {
      // If tasks were provided externally, just process them
      this.initializeGantt();
    }
  }

  /**
   * Load tasks dynamically from API based on filters and current 10-day window.
   */
  private loadTasks(): void {
    if (this.loading) {
      return;
    }
    if (!this.viewStartDate) {
      this.setInitialViewWindow();
    }

    this.loading = true;

    // Prepare filters for API call
    const apiFilters: any = {};
    
    // Convert filters to API format
    if (this.filters) {
      if (this.filters.taskId && Array.isArray(this.filters.taskId)) {
        apiFilters.taskId = { value: this.filters.taskId, matchMode: 'in' };
      }
      if (this.filters.name) {
        apiFilters.name = { value: this.filters.name, matchMode: 'contains' };
      }
      if (this.filters.status && Array.isArray(this.filters.status) && this.filters.status.length > 0) {
        apiFilters.status = { value: this.filters.status, matchMode: 'in' };
      }
      if (this.filters.priority && Array.isArray(this.filters.priority) && this.filters.priority.length > 0) {
        apiFilters.priority = { value: this.filters.priority, matchMode: 'in' };
      }
      if (this.filters.assignedTo) {
        if (Array.isArray(this.filters.assignedTo)) {
          const userIds = this.filters.assignedTo.map(u => u.userId || u);
          apiFilters['assignedToUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['assignedToUserVO.userId'] = { value: [this.filters.assignedTo.userId], matchMode: 'in' };
        }
      }
      if (this.filters.createdBy) {
        if (Array.isArray(this.filters.createdBy)) {
          const userIds = this.filters.createdBy.map(u => u.userId || u);
          apiFilters['createdByUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['createdByUserVO.userId'] = { value: [this.filters.createdBy.userId], matchMode: 'in' };
        }
      }
      if (this.filters.requestedBy) {
        if (Array.isArray(this.filters.requestedBy)) {
          const userIds = this.filters.requestedBy.map(u => u.userId || u);
          apiFilters['requestedByUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['requestedByUserVO.userId'] = { value: [this.filters.requestedBy.userId], matchMode: 'in' };
        }
      }
    }

    // Add taskTag filter if present
    if (this.taskTag) {
      apiFilters.tagList = { value: this.taskTag, matchMode: 'in' };
    }

    // Default sort
    const multiSortMeta = [
      { field: 'dateDue', order: -1 },
      { field: 'dateCreated', order: 0 }
    ];

    // const rangeStart = this.viewStartDate.clone().startOf('day');
    // const rangeEnd = this.viewStartDate.clone().add(DAYS_VIEW - 1, 'days').endOf('day');
    // const dateRange = [rangeStart.format('YYYY-MM-DD'), rangeEnd.format('YYYY-MM-DD')];

    this.taskService.findByFilterSummaryWithDateRange(
      0, // page
      1000, // size
      null,
      null,
      multiSortMeta,
      null,
      apiFilters,
      null // dateRange - startDate/endDate filters commented out
    ).pipe(first()).subscribe(
      apiResponse => {
        const loadedTasks = apiResponse.data.tasks || [];

        // Flatten tasks including children and remove duplicates by taskId
        const allTasks: Task[] = [];
        const taskIdSet = new Set<number>();
        
        loadedTasks.forEach(task => {
          // Add parent task if not already added
          if (task.taskId && !taskIdSet.has(task.taskId)) {
            allTasks.push(task);
            taskIdSet.add(task.taskId);
          }
          // Add children if they exist and haven't been added
          if (task.children && task.children.length > 0) {
            task.children.forEach(child => {
              if (child.taskId && !taskIdSet.has(child.taskId)) {
                allTasks.push(child);
                taskIdSet.add(child.taskId);
              }
            });
          }
        });

        // Update tasks array
        this.tasks = allTasks;

        // Process tasks for Gantt
        this.initializeGantt();

        // Emit that filters have been processed
        setTimeout(() => {
          this.filtersProcessed.emit();
        }, 100);

        this.loading = false;
      },
      error => {
        this.alertService.error(error);
        this.loading = false;
        this.ganttTasks = [];
      }
    );
  }

  initializeGantt() {
    if (!this.viewStartDate) {
      this.setInitialViewWindow();
    }
    this.startDate = this.viewStartDate.clone().startOf('day');
    this.endDate = this.viewStartDate.clone().add(DAYS_VIEW - 1, 'days').endOf('day');

    this.days = [];
    let current = this.startDate.clone();
    for (let i = 0; i < DAYS_VIEW; i++) {
      this.days.push(current.clone());
      current.add(1, 'day');
    }

    if (!this.tasks || this.tasks.length === 0) {
      this.ganttTasks = [];
      return;
    }

    const filteredTasks = this.applyFilters(this.tasks);
    const rangeStart = this.startDate;
    const rangeEnd = this.endDate;

    this.ganttTasks = filteredTasks
      .filter(task => {
        if (!task.dateDue) return false;
        const dueDay = this.getDueDateAsDay(task.dateDue);
        if (!dueDay) return false;
        return dueDay.isSameOrAfter(rangeStart, 'day') && dueDay.isSameOrBefore(rangeEnd, 'day');
      })
      .map(task => {
        const start = this.getDueDateAsDay(task.dateDue) || this.startDate.clone();
        const end = start.clone().add(1, 'day');
        return {
          task,
          start,
          end,
          duration: 1
        };
      });
  }

  private getDueDateAsDay(dateDue: any): moment.Moment | null {
    if (!dateDue) return null;
    const m = moment.utc(dateDue);
    if (!m.isValid()) return null;
    const dateStr = m.format('YYYY-MM-DD');
    return moment(dateStr).startOf('day');
  }


  getTaskPosition(task: any): number {
    if (!this.days.length) return 0;
    const daysDiff = task.start.diff(this.startDate, 'days');
    const pct = (daysDiff / this.days.length) * 100;
    return Math.max(0, Math.round(pct * 100) / 100);
  }

  getTaskWidth(task: any): number {
    if (!this.days.length) return 10;
    const pct = (task.duration / this.days.length) * 100;
    return Math.round(pct * 100) / 100;
  }
}

